import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import AdminLayout from "@/components/feature/AdminLayout";
import { supabase } from "@/lib/supabase";
import { articleToRow } from "@/hooks/useArticlesDB";
import ImageUploadField from "@/components/feature/ImageUploadField";
import { useAdminI18n } from "@/hooks/useAdminI18n";

/* ─── Types ─── */
interface DBCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
  is_active: boolean;
  subcategories?: { id: string; name: string; slug: string }[];
}

interface DBAuthor {
  name: string;
  slug: string;
  role: string;
  author_type: string;
  image: string;
}

export default function ArticleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useAdminI18n();
  const isEdit = !!id;
  const isDuplicate = new URLSearchParams(window.location.search).get("duplicate") === "1";

  // Core fields
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [categorySlug, setCategorySlug] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [authorSlug, setAuthorSlug] = useState("");
  const [tags, setTags] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [gallery, setGallery] = useState<string[]>([]);

  // Status & publishing
  const [status, setStatus] = useState<Article["status"]>("draft");
  const [publishedAt, setPublishedAt] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  // Special flags
  const [isFeatured, setIsFeatured] = useState(false);
  const [isBreaking, setIsBreaking] = useState(false);
  const [isTopNews, setIsTopNews] = useState(false);
  const [showOnHomepageSlider, setShowOnHomepageSlider] = useState(false);
  const [showInTopNewsColumn, setShowInTopNewsColumn] = useState(false);
  const [showInCategoryFeatured, setShowInCategoryFeatured] = useState(false);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [adsEnabled, setAdsEnabled] = useState(true);

  // Links
  const [sources, setSources] = useState<{ title: string; url: string }[]>([]);
  const [internalLinks, setInternalLinks] = useState<{ title: string; slug: string }[]>([]);
  const [externalLinks, setExternalLinks] = useState<{ title: string; url: string }[]>([]);

  // SEO
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [focusKeyword, setFocusKeyword] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [ogImage, setOgImage] = useState("");
  const [twitterImage, setTwitterImage] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [noIndex, setNoIndex] = useState(false);

  // UI state
  const [activeTab, setActiveTab] = useState("content");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // SEO generation state
  const [showSEOModal, setShowSEOModal] = useState(false);
  const [seoOverwriteAll, setSeoOverwriteAll] = useState(false);
  const [seoGenerated, setSeoGenerated] = useState(false);

  // SEO auto-prompt on publish
  const [showSEOPrompt, setShowSEOPrompt] = useState(false);
  const [pendingPublishStatus, setPendingPublishStatus] = useState<Article["status"] | null>(null);

  // Focus keyword suggestions
  const [suggestedKeywords, setSuggestedKeywords] = useState<{ word: string; score: number; type: "single" | "phrase" }[]>([]);
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);

  // DB data
  const [dbAuthors, setDbAuthors] = useState<DBAuthor[]>([]);
  const [dbCategories, setDbCategories] = useState<DBCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [authorsLoading, setAuthorsLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [authorsError, setAuthorsError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    const { data, error } = await supabase
      .from("categories")
      .select("id, name, slug, color, is_active, subcategories")
      .order("sort_order");
    if (error) {
      setCategoriesError(error.message);
    } else {
      setDbCategories((data ?? []) as DBCategory[]);
    }
    setCategoriesLoading(false);
  }, []);

  const fetchAuthors = useCallback(async () => {
    setAuthorsLoading(true);
    setAuthorsError(null);
    const { data, error } = await supabase
      .from("authors")
      .select("name, slug, role, author_type, image")
      .order("name");
    if (error) {
      setAuthorsError(error.message);
    } else {
      setDbAuthors((data ?? []) as DBAuthor[]);
    }
    setAuthorsLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchAuthors();
  }, [fetchCategories, fetchAuthors]);

  const activeCategories = dbCategories.filter((c) => c.is_active);

  // Generate slug from title
  const generateSlug = useCallback((text: string) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .substring(0, 80);
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      supabase.from("articles").select("*").eq("id", id).maybeSingle().then(({ data }) => {
        if (!data) return;
        const r = data as Record<string, unknown>;
        setTitle(r.title as string || "");
        setSubtitle(r.subtitle as string || "");
        setSlug(r.slug as string || "");
        setExcerpt(r.excerpt as string || "");
        setContent(r.content as string || "");
        setCategorySlug(r.category_slug as string || "");
        setSubcategory(r.subcategory as string || "");
        setAuthorSlug(r.author_slug as string || "");
        setTags(((r.tags as string[]) || []).join(", "));
        setImageUrl(r.image_url as string || "");
        setGallery((r.gallery as string[]) || []);
        setStatus(r.status as Article["status"] || "draft");
        setPublishedAt(r.published_at ? (r.published_at as string).slice(0, 16) : "");
        setScheduledAt(r.published_at && r.status === "scheduled" ? (r.published_at as string).slice(0, 16) : "");
        setIsFeatured(r.is_featured as boolean || false);
        setIsBreaking(r.is_breaking as boolean || false);
        setIsTopNews(r.is_top_news as boolean || false);
        setShowOnHomepageSlider(r.show_on_homepage_slider as boolean || false);
        setShowInTopNewsColumn(r.show_in_top_news_column as boolean || false);
        setShowInCategoryFeatured(r.show_in_category_featured as boolean || false);
        setCommentsEnabled(r.comments_enabled !== false);
        setAdsEnabled(r.ads_enabled !== false);
        setSources((r.sources as { title: string; url: string }[]) || []);
        setInternalLinks((r.internal_links as { title: string; slug: string }[]) || []);
        setExternalLinks((r.external_links as { title: string; url: string }[]) || []);
        setMetaTitle(r.meta_title as string || r.title as string || "");
        setMetaDescription(r.meta_description as string || (r.excerpt as string || "").slice(0, 160));
        setFocusKeyword(r.focus_keyword as string || "");
        setCanonicalUrl(r.canonical_url as string || "");
        setOgTitle(r.og_title as string || r.title as string || "");
        setOgDescription(r.og_description as string || (r.excerpt as string || "").slice(0, 160));
        setOgImage(r.og_image as string || r.image_url as string || "");
        setTwitterImage(r.twitter_image as string || r.image_url as string || "");
        setImageAlt(r.image_alt as string || "");
        setNoIndex(r.no_index as boolean || false);
      });
    }
  }, [isEdit, id]);

  // Auto-generate slug when title changes (only for new articles)
  useEffect(() => {
    if (!isEdit && title && !slug) {
      setSlug(generateSlug(title));
    }
  }, [title, isEdit, slug, generateSlug]);

  /* ─── Focus Keyword Suggestions ─── */
  const suggestFocusKeywords = useCallback(() => {
    const rawText = `${title} ${excerpt} ${content.replace(/<[^>]+>/g, " ")}`;

    const stopWords = new Set([
      "der","die","das","den","dem","des","ein","eine","einer","einen","einem","eines",
      "und","oder","aber","mit","von","zu","für","in","auf","an","bei","nach","wie",
      "so","dass","sich","ist","sind","war","wurde","wurden","hat","haben","kann",
      "können","werden","wird","nicht","als","auch","nur","noch","schon","dann","dort",
      "hier","wenn","weil","ob","was","wer","wo","wann","warum","durch","über","unter",
      "vor","hinter","neben","zwischen","während","gegen","ohne","um","bis","seit",
      "dieser","diese","dieses","jeder","jede","jedes","alle","viele","wenige","mehr",
      "meiste","andere","eigenen","selbst","mich","dich","ihn","sie","es","uns","euch",
      "ihnen","mir","dir","ihm","ihr","mein","dein","sein","unser","euer","meine",
      "deine","seine","ihre","unsere","eure","am","im","zum","zur","beim","vom","ins","aufs",
      "the","a","an","and","or","but","in","on","at","to","for","of","with","by",
      "from","as","is","are","was","were","be","been","have","has","had","do","does",
      "did","will","would","could","should","may","might","can","this","that","these",
      "those","i","you","he","she","it","we","they","me","him","her","us","them",
      "my","your","his","its","our","their","what","which","who","when","where","why",
      "how","all","any","both","each","few","more","most","other","some","such","no",
      "nor","not","only","own","same","so","than","too","very","just","now","then",
      "here","there","up","down","out","off","over","under","again","further","once",
      "also","about","into","through","during","before","after","above","below","between",
      "among","within","without","against","until","since","because","although","though",
      "while","if","unless","whether","whenever","wherever","however","therefore","thus",
      "hence","moreover","furthermore","nevertheless","nonetheless","otherwise","instead",
      "meanwhile","besides","except","including","regarding","concerning","according","due",
      "owing","thanks","regardless","notwithstanding","along","around","across","behind",
      "beyond","despite","inside","outside","upon","toward","towards","plus","minus","per",
      "via","like","unlike","near","next","past","round",
    ]);

    const cleanText = rawText
      .toLowerCase()
      .replace(/<[^>]+>/g, " ")
      .replace(/[^\w\säöüß]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const tokens = cleanText.split(/\s+/).filter((w) => w.length >= 3);
    const titleLower = title.toLowerCase();

    // Score unigrams
    const unigramScores: Record<string, number> = {};
    tokens.forEach((w, i) => {
      if (stopWords.has(w)) return;
      unigramScores[w] = (unigramScores[w] || 0) + 1;
      if (titleLower.includes(w)) unigramScores[w] += 3;
      if (i < 20) unigramScores[w] += 0.5;
    });

    // Score bigrams
    const bigramScores: Record<string, number> = {};
    for (let i = 0; i < tokens.length - 1; i++) {
      const w1 = tokens[i];
      const w2 = tokens[i + 1];
      if (stopWords.has(w1) || stopWords.has(w2)) continue;
      const bigram = `${w1} ${w2}`;
      bigramScores[bigram] = (bigramScores[bigram] || 0) + 2;
      if (titleLower.includes(w1) && titleLower.includes(w2)) bigramScores[bigram] += 4;
    }

    const ranked = [
      ...Object.entries(unigramScores).map(([word, score]) => ({ word, score, type: "single" as const })),
      ...Object.entries(bigramScores).map(([word, score]) => ({ word, score, type: "phrase" as const })),
    ]
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);

    setSuggestedKeywords(ranked);
    setShowKeywordSuggestions(true);
  }, [title, excerpt, content]);

  const applySuggestedKeyword = useCallback((keyword: string) => {
    setFocusKeyword(keyword);
    setShowKeywordSuggestions(false);
  }, []);

  /* ─── SEO Generation ─── */
  const generateSEO = useCallback((overwrite = false) => {
    const newSlug = !slug || overwrite ? generateSlug(title) : slug;
    const newMetaTitle = !metaTitle || overwrite ? title.slice(0, 60) : metaTitle;
    const sourceText = excerpt || content.replace(/<[^>]+>/g, " ").trim();
    const newMetaDesc = !metaDescription || overwrite
      ? sourceText.slice(0, 157) + (sourceText.length > 157 ? "..." : "")
      : metaDescription;

    // Extract focus keyword: first 1-3 meaningful words from title
    const keyword = !focusKeyword || overwrite
      ? title
          .toLowerCase()
          .replace(/[^\w\säöüß]/g, "")
          .split(/\s+/)
          .filter((w) => w.length > 2)
          .slice(0, 3)
          .join(" ")
      : focusKeyword;

    const newImageAlt = !imageAlt || overwrite ? title : imageAlt;

    setSlug(newSlug);
    setMetaTitle(newMetaTitle);
    setMetaDescription(newMetaDesc);
    setFocusKeyword(keyword);
    setImageAlt(newImageAlt);
    setOgTitle(newMetaTitle);
    setOgDescription(newMetaDesc);
    setOgImage(imageUrl);
    setTwitterImage(imageUrl);
    setCanonicalUrl(`https://helvetik-journal.ch/artikel/${newSlug}`);
    setSeoGenerated(true);
    setTimeout(() => setSeoGenerated(false), 2500);
  }, [title, slug, metaTitle, metaDescription, focusKeyword, imageAlt, excerpt, content, imageUrl, generateSlug]);

  const openSEOModal = useCallback(() => {
    setSeoOverwriteAll(false);
    setShowSEOModal(true);
  }, []);

  const confirmSEOG = useCallback(() => {
    generateSEO(seoOverwriteAll);
    setShowSEOModal(false);
  }, [generateSEO, seoOverwriteAll]);

  const handleSave = async (targetStatus: Article["status"]) => {
    if (!title.trim() || !slug.trim()) return;
    setSaving(true);
    setSaveError(null);

    const articleData = {
      title, subtitle, slug, excerpt, content,
      category: activeCategories.find((c) => c.slug === categorySlug)?.name || categorySlug,
      categorySlug, subcategory,
      author: dbAuthors.find((a) => a.slug === authorSlug)?.name || authorSlug,
      authorSlug,
      imageUrl, gallery,
      publishedAt: publishedAt || new Date().toISOString(),
      readTime: `${Math.max(1, Math.ceil(content.length / 1200))} ${t("articleForm.min")}`,
      views: 0,
      tags: tags.split(",").map((tg) => tg.trim()).filter(Boolean),
      status: targetStatus,
      isFeatured, isBreaking, isTopNews,
      isOpinion: false,
      isColumn: dbAuthors.find((a) => a.slug === authorSlug)?.author_type === "kolumnist",
      showOnHomepageSlider, showInTopNewsColumn, showInCategoryFeatured,
      commentsEnabled, adsEnabled,
      sources, internalLinks, externalLinks,
      metaTitle, metaDescription, focusKeyword, canonicalUrl,
      ogTitle, ogDescription, ogImage, twitterImage, imageAlt, noIndex,
    };

    const row = articleToRow(articleData);

    if (isEdit && id) {
      const { error } = await supabase.from("articles").update(row).eq("id", id);
      if (error) { setSaveError(error.message); setSaving(false); return; }
    } else {
      const { data, error } = await supabase.from("articles").insert(row).select("id").maybeSingle();
      if (error) { setSaveError(error.message); setSaving(false); return; }
      if (data) navigate(`/admin/artikel-bearbeiten/${(data as Record<string, unknown>).id}`, { replace: true });
    }

    setStatus(targetStatus);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  /* ─── SEO Score (live) ─── */
  const computeSEOScore = useCallback(() => {
    let score = 0;
    // Meta title (max 25)
    if (metaTitle.trim()) { score += 20; if (metaTitle.length >= 30 && metaTitle.length <= 60) score += 5; }
    // Meta description (max 25)
    if (metaDescription.trim()) { score += 20; if (metaDescription.length >= 120 && metaDescription.length <= 160) score += 5; }
    // Focus keyword (max 15)
    if (focusKeyword.trim()) score += 15;
    // Slug (max 15)
    if (slug.trim()) score += 15;
    // Image alt (max 10)
    if (imageAlt.trim()) score += 10;
    // OG title / desc (max 5)
    if (ogTitle.trim()) score += 3;
    if (ogDescription.trim()) score += 2;
    // Canonical URL (max 5)
    if (canonicalUrl.trim()) score += 5;
    return Math.min(100, score);
  }, [metaTitle, metaDescription, focusKeyword, slug, imageAlt, ogTitle, ogDescription, canonicalUrl]);

  const seoScore = computeSEOScore();

  const scoreLabel = (s: number) => {
    if (s >= 90) return { label: t("articleForm.seoScoreExcellent"), color: "text-emerald-600", bg: "bg-emerald-500" };
    if (s >= 70) return { label: t("articleForm.seoScoreGood"), color: "text-sky-600", bg: "bg-sky-500" };
    if (s >= 50) return { label: t("articleForm.seoScoreFair"), color: "text-amber-600", bg: "bg-amber-500" };
    return { label: t("articleForm.seoScorePoor"), color: "text-red-600", bg: "bg-red-500" };
  };

  /* ─── SEO Auto-prompt on publish ─── */
  const hasEmptySEOFields = useCallback(() => {
    return !metaTitle.trim() || !metaDescription.trim() || !focusKeyword.trim() || !imageAlt.trim() || !slug.trim();
  }, [metaTitle, metaDescription, focusKeyword, imageAlt, slug]);

  const handlePublishWithSEOCheck = useCallback((targetStatus: Article["status"]) => {
    if (targetStatus === "published" && hasEmptySEOFields()) {
      setPendingPublishStatus(targetStatus);
      setShowSEOPrompt(true);
      return;
    }
    handleSave(targetStatus);
  }, [hasEmptySEOFields, handleSave]);

  const confirmSEOPromptGenerate = useCallback(() => {
    generateSEO(true);
    setShowSEOPrompt(false);
    if (pendingPublishStatus) {
      handleSave(pendingPublishStatus);
      setPendingPublishStatus(null);
    }
  }, [generateSEO, pendingPublishStatus, handleSave]);

  const skipSEOPrompt = useCallback(() => {
    setShowSEOPrompt(false);
    if (pendingPublishStatus) {
      handleSave(pendingPublishStatus);
      setPendingPublishStatus(null);
    }
  }, [pendingPublishStatus, handleSave]);

  const handleDuplicate = () => {
    navigate(`/admin/artikel-neu?duplicate=1&from=${id}`);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (id) await supabase.from("articles").delete().eq("id", id);
    setShowDeleteModal(false);
    navigate("/admin/artikel");
  };

  // Rich text toolbar
  const insertFormat = (format: string) => {
    const textarea = document.getElementById("content-editor") as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    let replacement = selected;

    switch (format) {
      case "bold": replacement = `<strong>${selected || "Text"}</strong>`; break;
      case "italic": replacement = `<em>${selected || "Text"}</em>`; break;
      case "heading": replacement = `\n<h2>${selected || "Heading"}</h2>\n`; break;
      case "quote": replacement = `\n<blockquote>${selected || "Quote"}</blockquote>\n`; break;
      case "link": replacement = `<a href="https://">${selected || "Link"}</a>`; break;
      case "list": replacement = `\n<ul>\n  <li>${selected || "Item"}</li>\n  <li>Item 2</li>\n</ul>\n`; break;
      case "paragraph": replacement = `\n<p>${selected || "Paragraph"}</p>\n`; break;
    }

    const newContent = content.substring(0, start) + replacement + content.substring(end);
    setContent(newContent);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + replacement.length, start + replacement.length);
    }, 0);
  };

  const addGalleryImage = () => setGallery([...gallery, ""]);
  const updateGalleryImage = (index: number, url: string) => {
    const g = [...gallery]; g[index] = url; setGallery(g);
  };
  const removeGalleryImage = (index: number) => setGallery(gallery.filter((_, i) => i !== index));

  const addSource = () => setSources([...sources, { title: "", url: "" }]);
  const updateSource = (index: number, field: "title" | "url", value: string) => {
    const s = [...sources]; s[index][field] = value; setSources(s);
  };
  const removeSource = (index: number) => setSources(sources.filter((_, i) => i !== index));

  const addInternalLink = () => setInternalLinks([...internalLinks, { title: "", slug: "" }]);
  const updateInternalLink = (index: number, field: "title" | "slug", value: string) => {
    const l = [...internalLinks]; l[index][field] = value; setInternalLinks(l);
  };
  const removeInternalLink = (index: number) => setInternalLinks(internalLinks.filter((_, i) => i !== index));

  const addExternalLink = () => setExternalLinks([...externalLinks, { title: "", url: "" }]);
  const updateExternalLink = (index: number, field: "title" | "url", value: string) => {
    const l = [...externalLinks]; l[index][field] = value; setExternalLinks(l);
  };
  const removeExternalLink = (index: number) => setExternalLinks(externalLinks.filter((_, i) => i !== index));

  const selectedAuthor = dbAuthors.find((a) => a.slug === authorSlug);
  const selectedCategory = activeCategories.find((c) => c.slug === categorySlug);

  const statusKeys: Article["status"][] = ["draft", "pending_review", "published", "scheduled", "archived"];
  const statusColors: Record<Article["status"], string> = {
    draft: "bg-gray-100 text-gray-700",
    pending_review: "bg-amber-100 text-amber-700",
    published: "bg-emerald-100 text-emerald-700",
    scheduled: "bg-sky-100 text-sky-700",
    archived: "bg-slate-100 text-slate-700",
  };

  const seoTitle = metaTitle || title || "—";
  const seoDesc = metaDescription || excerpt || "—";
  const seoUrl = `https://helvetik-journal.ch/artikel/${slug || "artikel-slug"}`;

  const tabs = [
    { id: "content", label: t("articleForm.tab.content"), icon: "ri-file-text-line" },
    { id: "media", label: t("articleForm.tab.media"), icon: "ri-image-line" },
    { id: "settings", label: t("articleForm.tab.settings"), icon: "ri-settings-3-line" },
    { id: "seo", label: t("articleForm.tab.seo"), icon: "ri-search-line" },
    { id: "links", label: t("articleForm.tab.links"), icon: "ri-links-line" },
  ];

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-hj-gray mb-4">
          <Link to="/admin/artikel" className="hover:text-hj-red transition-colors">
            {t("articleForm.breadcrumbArticles")}
          </Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-hj-dark">
            {isEdit ? (isDuplicate ? t("articleForm.duplicate") : t("articleForm.editArticle")) : t("articleForm.newArticle")}
          </span>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-hj-dark">
              {isEdit ? (isDuplicate ? t("articleForm.duplicate") : t("articleForm.editArticle")) : t("articleForm.newArticle")}
            </h1>
            {status && (
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[status]}`}>
                {t(`articleForm.status.${status}`)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {saved && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <i className="ri-check-line"></i>
                {t("articleForm.saved")}
              </span>
            )}
            {isEdit && (
              <>
                <button
                  onClick={() => setShowPreview(true)}
                  className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-eye-line"></i>
                  {t("articleForm.preview")}
                </button>
                <button
                  onClick={handleDuplicate}
                  className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-copy-line"></i>
                  {t("articleForm.duplicate")}
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                  {t("articleForm.delete")}
                </button>
              </>
            )}
            <button
              onClick={() => navigate("/admin/artikel")}
              className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("articleForm.cancel")}
            </button>
            <button
              onClick={() => handleSave("draft")}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {saving ? t("articleForm.saving") : t("articleForm.saveDraft")}
            </button>
            <button
              onClick={() => handlePublishWithSEOCheck("published")}
              disabled={saving}
              className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line"></i>
              {saving ? t("articleForm.publishing") : t("articleForm.publish")}
            </button>
          </div>
        </div>

        {/* Save Error */}
        {saveError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
            <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
            <div><strong>{t("articleForm.saveError")}</strong> {saveError}</div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-hj-gray-light p-1 rounded-lg w-fit overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                activeTab === tab.id ? "bg-white text-hj-dark shadow-sm" : "text-hj-gray hover:text-hj-dark"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === "content" && (
              <>
                {/* Title */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <label className="block text-sm font-semibold text-hj-dark mb-2">{t("articleForm.title")} *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    placeholder={t("articleForm.titlePlaceholder")}
                  />
                </div>

                {/* Subtitle */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <label className="block text-sm font-semibold text-hj-dark mb-2">{t("articleForm.subtitle")}</label>
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    placeholder={t("articleForm.subtitlePlaceholder")}
                  />
                </div>

                {/* Slug */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <label className="block text-sm font-semibold text-hj-dark mb-2">{t("articleForm.slug")} *</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-hj-gray shrink-0">/artikel/</span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      className="flex-1 px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-mono"
                      placeholder={t("articleForm.slugPlaceholder")}
                    />
                  </div>
                </div>

                {/* Excerpt */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <label className="block text-sm font-semibold text-hj-dark mb-2">{t("articleForm.excerpt")} *</label>
                  <textarea
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                    placeholder={t("articleForm.excerptPlaceholder")}
                  />
                  <p className="text-xs text-hj-gray mt-1">{excerpt.length}/500</p>
                </div>

                {/* Rich Text Editor */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <label className="block text-sm font-semibold text-hj-dark mb-2">{t("articleForm.content")} *</label>
                  <div className="flex items-center gap-1 mb-3 p-2 bg-hj-gray-light rounded-md flex-wrap">
                    {[
                      { fmt: "bold", icon: "ri-bold" },
                      { fmt: "italic", icon: "ri-italic" },
                      { fmt: "heading", icon: "ri-heading" },
                      { fmt: "paragraph", icon: "ri-paragraph" },
                      { fmt: "quote", icon: "ri-double-quotes-l" },
                      { fmt: "list", icon: "ri-list-unordered" },
                      { fmt: "link", icon: "ri-link" },
                    ].map(({ fmt, icon }) => (
                      <button
                        key={fmt}
                        onClick={() => insertFormat(fmt)}
                        className="w-8 h-8 flex items-center justify-center rounded hover:bg-white transition-colors cursor-pointer"
                      >
                        <i className={`${icon} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                  <textarea
                    id="content-editor"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={16}
                    className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none font-mono leading-relaxed"
                    placeholder={t("articleForm.contentPlaceholder")}
                  />
                  <p className="text-xs text-hj-gray mt-1">{content.length} {t("articleForm.contentChars")}</p>
                </div>
              </>
            )}

            {activeTab === "media" && (
              <>
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <ImageUploadField
                    label={t("articleForm.featuredImage")}
                    value={imageUrl}
                    onChange={setImageUrl}
                    placeholder="https://..."
                  />
                </div>
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-semibold text-hj-dark">{t("articleForm.gallery")}</label>
                    <button
                      onClick={addGalleryImage}
                      className="text-sm text-hj-red hover:text-hj-red-light transition-colors flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      {t("articleForm.addImage")}
                    </button>
                  </div>
                  <div className="space-y-5">
                    {gallery.map((url, index) => (
                      <div key={index} className="border border-hj-gray-border rounded-lg p-4 relative">
                        <button
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                        <ImageUploadField
                          label={`${t("articleForm.gallery")} ${index + 1}`}
                          value={url}
                          onChange={(newUrl) => updateGalleryImage(index, newUrl)}
                          placeholder="https://..."
                        />
                      </div>
                    ))}
                    {gallery.length === 0 && (
                      <p className="text-sm text-hj-gray text-center py-4">{t("articleForm.noGallery")}</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === "settings" && (
              <>
                {/* Category & Author */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4">{t("articleForm.categoryAuthor")}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.category")} *</label>
                      {categoriesLoading ? (
                        <div className="w-full h-10 bg-gray-100 rounded-md animate-pulse"></div>
                      ) : categoriesError ? (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>
                            {t("articleForm.categoriesError")}: {categoriesError}
                          </p>
                          <button onClick={fetchCategories} className="text-xs text-hj-red mt-1 hover:underline cursor-pointer">
                            {t("common.refresh")}
                          </button>
                        </div>
                      ) : activeCategories.length === 0 ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-xs text-amber-700 mb-2">{t("articleForm.noCategories")}</p>
                          <Link
                            to="/admin/kategorien"
                            className="inline-flex items-center gap-1 text-xs font-medium text-hj-red hover:text-hj-red-light transition-colors"
                          >
                            <i className="ri-add-line"></i>
                            {t("articleForm.createCategory")}
                          </Link>
                        </div>
                      ) : (
                        <>
                          <select
                            value={categorySlug}
                            onChange={(e) => setCategorySlug(e.target.value)}
                            className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
                          >
                            <option value="">{t("articleForm.selectCategory")}</option>
                            {activeCategories.map((cat) => (
                              <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                            ))}
                          </select>
                          <Link
                            to="/admin/kategorien"
                            className="inline-flex items-center gap-1 text-xs text-hj-gray hover:text-hj-red transition-colors mt-1.5"
                          >
                            <i className="ri-add-line"></i>
                            {t("articleForm.createCategory")}
                          </Link>
                        </>
                      )}
                    </div>

                    {/* Subcategory */}
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.subcategory")}</label>
                      <input
                        type="text"
                        value={subcategory}
                        onChange={(e) => setSubcategory(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="z.B. Umweltpolitik"
                      />
                    </div>

                    {/* Author */}
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.author")} *</label>
                      {authorsLoading ? (
                        <div className="w-full h-10 bg-gray-100 rounded-md animate-pulse"></div>
                      ) : authorsError ? (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>
                            {t("articleForm.authorsError")}: {authorsError}
                          </p>
                          <button onClick={fetchAuthors} className="text-xs text-hj-red mt-1 hover:underline cursor-pointer">
                            {t("common.refresh")}
                          </button>
                        </div>
                      ) : dbAuthors.length === 0 ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-xs text-amber-700 mb-2">{t("articleForm.noAuthors")}</p>
                          <Link
                            to="/admin/autoren"
                            className="inline-flex items-center gap-1 text-xs font-medium text-hj-red hover:text-hj-red-light transition-colors"
                          >
                            <i className="ri-add-line"></i>
                            {t("articleForm.createAuthor")}
                          </Link>
                        </div>
                      ) : (
                        <>
                          <select
                            value={authorSlug}
                            onChange={(e) => setAuthorSlug(e.target.value)}
                            className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
                          >
                            <option value="">{t("articleForm.selectAuthor")}</option>
                            {(["redaktion", "autor", "kolumnist", "gastautor", "editor"] as const).map((type) => {
                              const group = dbAuthors.filter((a) => a.author_type === type);
                              if (group.length === 0) return null;
                              const groupLabel =
                                type === "redaktion" ? "Redaktion" :
                                type === "autor" ? "Autoren" :
                                type === "kolumnist" ? "Kolumnisten" :
                                type === "gastautor" ? "Gastautoren" : "Editoren";
                              return (
                                <optgroup key={type} label={groupLabel}>
                                  {group.map((auth) => (
                                    <option key={auth.slug} value={auth.slug}>{auth.name} — {auth.role}</option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </select>
                          {selectedAuthor?.author_type === "kolumnist" && (
                            <p className="text-xs text-hj-red mt-1.5 flex items-center gap-1">
                              <i className="ri-user-star-line"></i>
                              {t("articleForm.columnistNote")}
                            </p>
                          )}
                          <Link
                            to="/admin/autoren"
                            className="inline-flex items-center gap-1 text-xs text-hj-gray hover:text-hj-red transition-colors mt-1.5"
                          >
                            <i className="ri-add-line"></i>
                            {t("articleForm.createAuthor")}
                          </Link>
                        </>
                      )}
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.tags")}</label>
                      <input
                        type="text"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder={t("articleForm.tagsPlaceholder")}
                      />
                    </div>
                  </div>
                </div>

                {/* Status & Publishing */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4">{t("articleForm.statusPublishing")}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.status")}</label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as Article["status"])}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
                      >
                        {statusKeys.map((s) => (
                          <option key={s} value={s}>{t(`articleForm.status.${s}`)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.publishDate")}</label>
                      <input
                        type="datetime-local"
                        value={publishedAt}
                        onChange={(e) => setPublishedAt(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      />
                    </div>
                  </div>
                  {status === "scheduled" && (
                    <div className="mt-4 p-3 bg-sky-50 rounded-md border border-sky-200">
                      <p className="text-sm text-sky-700 flex items-center gap-2">
                        <i className="ri-time-line"></i>
                        {t("articleForm.scheduledNote")} {scheduledAt ? new Date(scheduledAt).toLocaleString("de-CH") : ""}
                      </p>
                    </div>
                  )}
                </div>

                {/* Special Flags */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4">{t("articleForm.specialFlags")}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { label: t("articleForm.flag.featured"), state: isFeatured, set: setIsFeatured },
                      { label: t("articleForm.flag.breaking"), state: isBreaking, set: setIsBreaking },
                      { label: t("articleForm.flag.topNews"), state: isTopNews, set: setIsTopNews },
                      { label: t("articleForm.flag.homepageSlider"), state: showOnHomepageSlider, set: setShowOnHomepageSlider },
                      { label: t("articleForm.flag.topNewsColumn"), state: showInTopNewsColumn, set: setShowInTopNewsColumn },
                      { label: t("articleForm.flag.categoryFeatured"), state: showInCategoryFeatured, set: setShowInCategoryFeatured },
                    ].map((flag) => (
                      <label key={flag.label} className="flex items-center gap-3 p-3 rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={flag.state}
                          onChange={(e) => flag.set(e.target.checked)}
                          className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                        />
                        <span className="text-sm text-hj-dark">{flag.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4">{t("articleForm.options")}</h3>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
                      <div>
                        <span className="text-sm font-medium text-hj-dark">{t("articleForm.commentsEnabled")}</span>
                        <p className="text-xs text-hj-gray">{t("articleForm.commentsEnabledDesc")}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={commentsEnabled}
                        onChange={(e) => setCommentsEnabled(e.target.checked)}
                        className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                      />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
                      <div>
                        <span className="text-sm font-medium text-hj-dark">{t("articleForm.adsEnabled")}</span>
                        <p className="text-xs text-hj-gray">{t("articleForm.adsEnabledDesc")}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={adsEnabled}
                        onChange={(e) => setAdsEnabled(e.target.checked)}
                        className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                      />
                    </label>
                  </div>
                </div>
              </>
            )}

            {activeTab === "seo" && (
              <>
                {/* SEO Auto-generate Button */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-hj-dark">{t("articleForm.seoGenerate")}</h3>
                      <p className="text-xs text-hj-gray mt-1">{t("articleForm.seoGenerateDesc")}</p>
                    </div>
                    <button
                      onClick={openSEOModal}
                      disabled={!title.trim()}
                      className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-magic-line"></i>
                      {t("articleForm.seoGenerate")}
                    </button>
                  </div>
                  {seoGenerated && (
                    <div className="mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700 flex items-center gap-2">
                      <i className="ri-check-line"></i>
                      {t("articleForm.seoGenerated")}
                    </div>
                  )}
                </div>

                {/* SEO Score Gauge */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-hj-dark flex items-center gap-2">
                        <i className="ri-bar-chart-line text-hj-gray"></i>
                        {t("articleForm.seoScoreTitle")}
                      </h3>
                      <p className="text-xs text-hj-gray mt-0.5">{t("articleForm.seoScoreDesc")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-bold ${scoreLabel(seoScore).color}`}>{seoScore}</span>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${seoScore >= 90 ? "bg-emerald-100 text-emerald-700" : seoScore >= 70 ? "bg-sky-100 text-sky-700" : seoScore >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {scoreLabel(seoScore).label}
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ease-out ${scoreLabel(seoScore).bg}`}
                      style={{ width: `${seoScore}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-[11px] text-hj-gray">
                    <span>0</span>
                    <span>50</span>
                    <span>100</span>
                  </div>
                </div>

                {/* SEO Warnings */}
                {(() => {
                  const warnings: { type: "error" | "warning"; msg: string }[] = [];
                  if (!metaTitle) warnings.push({ type: "error", msg: `${t("articleForm.seoTitle")} fehlt` });
                  else if (metaTitle.length > 60) warnings.push({ type: "warning", msg: `${t("articleForm.seoTitle")} zu lang (${metaTitle.length}/60)` });
                  if (!metaDescription) warnings.push({ type: "error", msg: `${t("articleForm.seoDescription")} fehlt` });
                  else if (metaDescription.length > 160) warnings.push({ type: "warning", msg: `${t("articleForm.seoDescription")} zu lang (${metaDescription.length}/160)` });
                  if (!focusKeyword) warnings.push({ type: "warning", msg: `${t("articleForm.focusKeyword")} fehlt` });
                  if (!imageAlt) warnings.push({ type: "warning", msg: `${t("articleForm.imageAlt")} fehlt` });
                  if (warnings.length === 0) return (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-lg"></i>
                      <p className="text-sm text-emerald-700 font-medium">{t("articleForm.seoOk")}</p>
                    </div>
                  );
                  return (
                    <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                      <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2">
                        <i className="ri-alert-line text-amber-500"></i>
                        {t("articleForm.seoWarnings")} ({warnings.length})
                      </h3>
                      <div className="space-y-2">
                        {warnings.map((w, i) => (
                          <div key={i} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${w.type === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                            <i className={`text-xs ${w.type === "error" ? "ri-close-circle-line" : "ri-alert-line"}`}></i>
                            {w.msg}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Google Preview */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4 flex items-center gap-2">
                    <i className="ri-google-line text-hj-gray"></i>
                    {t("articleForm.googlePreview")}
                  </h3>
                  <div className="p-4 bg-white rounded-md border border-hj-gray-border max-w-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 rounded-full bg-hj-gray-light"></div>
                      <p className="text-xs text-hj-gray truncate">{seoUrl}</p>
                    </div>
                    <p className={`text-base text-[#1a0dab] hover:underline cursor-pointer line-clamp-1 ${metaTitle.length > 60 ? "text-red-500" : ""}`}>
                      {seoTitle}
                    </p>
                    <p className={`text-sm mt-1 line-clamp-2 ${metaDescription.length > 160 ? "text-red-500" : "text-hj-gray"}`}>
                      {seoDesc}
                    </p>
                  </div>
                </div>

                {/* Meta Fields */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4">Meta-Tags</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.seoTitle")}</label>
                      <input
                        type="text"
                        value={metaTitle}
                        onChange={(e) => setMetaTitle(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red ${metaTitle.length > 60 ? "border-red-400" : "border-hj-gray-border"}`}
                        placeholder={`${t("articleForm.seoTitle")} (max. 60)`}
                      />
                      <p className={`text-xs mt-1 font-medium ${metaTitle.length > 60 ? "text-red-500" : metaTitle.length > 50 ? "text-amber-500" : "text-emerald-600"}`}>
                        {metaTitle.length}/60
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.seoDescription")}</label>
                      <textarea
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        rows={3}
                        maxLength={200}
                        className={`w-full px-3 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none ${metaDescription.length > 160 ? "border-red-400" : "border-hj-gray-border"}`}
                        placeholder={`${t("articleForm.seoDescription")} (max. 160)`}
                      />
                      <p className={`text-xs mt-1 font-medium ${metaDescription.length > 160 ? "text-red-500" : metaDescription.length > 140 ? "text-amber-500" : "text-emerald-600"}`}>
                        {metaDescription.length}/160
                      </p>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.focusKeyword")}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={focusKeyword}
                          onChange={(e) => { setFocusKeyword(e.target.value); setShowKeywordSuggestions(false); }}
                          className="flex-1 px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder={t("articleForm.focusKeyword")}
                        />
                        <button
                          type="button"
                          onClick={suggestFocusKeywords}
                          disabled={!title.trim() && !content.trim()}
                          className="shrink-0 px-3 py-2.5 text-sm font-medium text-hj-red border border-hj-red/30 rounded-md hover:bg-hj-red/5 transition-colors disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                          title={t("articleForm.suggestKeywords")}
                        >
                          <i className="ri-lightbulb-line"></i>
                          <span className="hidden sm:inline">{t("articleForm.suggestKeywords")}</span>
                        </button>
                      </div>

                      {/* Keyword suggestion panel */}
                      {showKeywordSuggestions && suggestedKeywords.length > 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-hj-gray-border rounded-lg shadow-lg overflow-hidden">
                          <div className="px-3 py-2 bg-hj-gray-light border-b border-hj-gray-border flex items-center justify-between">
                            <span className="text-xs font-semibold text-hj-dark">{t("articleForm.suggestedKeywords")}</span>
                            <button
                              onClick={() => setShowKeywordSuggestions(false)}
                              className="w-5 h-5 flex items-center justify-center rounded hover:bg-white transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-xs text-hj-gray"></i>
                            </button>
                          </div>
                          <div className="p-2 space-y-1">
                            {suggestedKeywords.map((k, i) => (
                              <button
                                key={i}
                                onClick={() => applySuggestedKeyword(k.word)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-hj-gray-light transition-colors text-left cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${k.type === "phrase" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                                    {k.type === "phrase" ? "2×" : "1×"}
                                  </span>
                                  <span className="text-sm text-hj-dark font-medium">{k.word}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-hj-red rounded-full"
                                      style={{ width: `${Math.min(100, (k.score / (suggestedKeywords[0]?.score || 1)) * 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-hj-gray w-6 text-right">{k.score}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                          <p className="px-3 py-2 text-[11px] text-hj-gray border-t border-hj-gray-border">
                            {t("articleForm.suggestKeywordsHint")}
                          </p>
                        </div>
                      )}
                      {showKeywordSuggestions && suggestedKeywords.length === 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-hj-gray-border rounded-lg shadow-lg p-4 text-center">
                          <p className="text-sm text-hj-gray">{t("articleForm.noKeywordSuggestions")}</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.canonicalUrl")}</label>
                      <input
                        type="url"
                        value={canonicalUrl}
                        onChange={(e) => setCanonicalUrl(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder={`https://helvetik-journal.ch/artikel/${slug || "slug"}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">{t("articleForm.imageAlt")}</label>
                      <input
                        type="text"
                        value={imageAlt}
                        onChange={(e) => setImageAlt(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder={t("articleForm.imageAlt")}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-md bg-hj-gray-light">
                      <div>
                        <span className="text-sm font-medium text-hj-dark">{t("articleForm.noIndex")}</span>
                        <p className="text-xs text-hj-gray mt-0.5">{t("articleForm.noIndexDesc")}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={noIndex}
                        onChange={(e) => setNoIndex(e.target.checked)}
                        className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                      />
                    </div>
                  </div>
                </div>

                {/* Open Graph */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4 flex items-center gap-2">
                    <i className="ri-share-line text-hj-gray"></i>
                    {t("articleForm.ogSection")}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">OG-Titel</label>
                      <input type="text" value={ogTitle} onChange={(e) => setOgTitle(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="Open Graph Titel" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">OG-Beschreibung</label>
                      <textarea value={ogDescription} onChange={(e) => setOgDescription(e.target.value)} rows={2} maxLength={300}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                        placeholder="Open Graph Beschreibung" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1.5">OG-Bild URL</label>
                      <input type="url" value={ogImage} onChange={(e) => setOgImage(e.target.value)}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="https://... (1200x630px)" />
                    </div>
                    {(ogImage || imageUrl) && (
                      <div className="p-3 bg-hj-gray-light rounded-md">
                        <div className="w-full max-w-sm aspect-[1200/630] overflow-hidden rounded-md">
                          <img src={ogImage || imageUrl} alt="OG Preview" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Twitter Card */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <h3 className="text-sm font-semibold text-hj-dark mb-4 flex items-center gap-2">
                    <i className="ri-twitter-x-line text-hj-gray"></i>
                    {t("articleForm.twitterSection")}
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5">Twitter Card Bild</label>
                    <input type="url" value={twitterImage} onChange={(e) => setTwitterImage(e.target.value)}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder="https://... (1200x600px)" />
                  </div>
                </div>
              </>
            )}

            {activeTab === "links" && (
              <>
                {/* Sources */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-hj-dark">{t("articleForm.sources")}</h3>
                    <button onClick={addSource} className="text-sm text-hj-red hover:text-hj-red-light transition-colors flex items-center gap-1 cursor-pointer">
                      <i className="ri-add-line"></i> {t("articleForm.addSource")}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {sources.map((source, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input type="text" value={source.title} onChange={(e) => updateSource(index, "title", e.target.value)}
                          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder="Titel" />
                        <input type="url" value={source.url} onChange={(e) => updateSource(index, "url", e.target.value)}
                          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder="https://..." />
                        <button onClick={() => removeSource(index)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors shrink-0 cursor-pointer">
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    ))}
                    {sources.length === 0 && <p className="text-sm text-hj-gray text-center py-4">{t("articleForm.noSources")}</p>}
                  </div>
                </div>

                {/* Internal Links */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-hj-dark">{t("articleForm.internalLinks")}</h3>
                    <button onClick={addInternalLink} className="text-sm text-hj-red hover:text-hj-red-light transition-colors flex items-center gap-1 cursor-pointer">
                      <i className="ri-add-line"></i> {t("articleForm.addInternalLink")}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {internalLinks.map((link, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input type="text" value={link.title} onChange={(e) => updateInternalLink(index, "title", e.target.value)}
                          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder="Titel" />
                        <input type="text" value={link.slug} onChange={(e) => updateInternalLink(index, "slug", e.target.value)}
                          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder="artikel-slug" />
                        <button onClick={() => removeInternalLink(index)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors shrink-0 cursor-pointer">
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    ))}
                    {internalLinks.length === 0 && <p className="text-sm text-hj-gray text-center py-4">{t("articleForm.noInternalLinks")}</p>}
                  </div>
                </div>

                {/* External Links */}
                <div className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-hj-dark">{t("articleForm.externalLinks")}</h3>
                    <button onClick={addExternalLink} className="text-sm text-hj-red hover:text-hj-red-light transition-colors flex items-center gap-1 cursor-pointer">
                      <i className="ri-add-line"></i> {t("articleForm.addExternalLink")}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {externalLinks.map((link, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input type="text" value={link.title} onChange={(e) => updateExternalLink(index, "title", e.target.value)}
                          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder="Titel" />
                        <input type="url" value={link.url} onChange={(e) => updateExternalLink(index, "url", e.target.value)}
                          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                          placeholder="https://..." />
                        <button onClick={() => removeExternalLink(index)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors shrink-0 cursor-pointer">
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    ))}
                    {externalLinks.length === 0 && <p className="text-sm text-hj-gray text-center py-4">{t("articleForm.noExternalLinks")}</p>}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <h3 className="text-sm font-semibold text-hj-dark mb-4">{t("articleForm.articleInfo")}</h3>
              <div className="space-y-3 text-sm">
                {selectedAuthor && (
                  <div className="flex items-center gap-3">
                    {selectedAuthor.image && (
                      <img src={selectedAuthor.image} alt={selectedAuthor.name} className="w-10 h-10 rounded-full object-cover" />
                    )}
                    <div>
                      <p className="font-medium text-hj-dark">{selectedAuthor.name}</p>
                      <p className="text-hj-gray text-xs">{selectedAuthor.role}</p>
                    </div>
                  </div>
                )}
                {selectedCategory && (
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCategory.color }}></span>
                    <span className="text-hj-dark">{selectedCategory.name}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-hj-gray-border">
                  <p className="text-hj-gray">{t("articleForm.readTime")} <span className="text-hj-dark font-medium">{Math.max(1, Math.ceil(content.length / 1200))} {t("articleForm.min")}</span></p>
                  <p className="text-hj-gray mt-1">{t("articleForm.wordCount")} <span className="text-hj-dark font-medium">{content.split(/\s+/).filter(Boolean).length}</span></p>
                </div>
              </div>
            </div>

            {/* Status Summary */}
            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <h3 className="text-sm font-semibold text-hj-dark mb-3">{t("articleForm.statusSummary")}</h3>
              <div className="space-y-2">
                {[
                  { label: t("articleForm.flag.featured"), active: isFeatured },
                  { label: t("articleForm.flag.breaking"), active: isBreaking },
                  { label: t("articleForm.flag.topNews"), active: isTopNews },
                  { label: t("articleForm.flag.homepageSlider"), active: showOnHomepageSlider },
                  { label: t("articleForm.commentsEnabled"), active: commentsEnabled },
                  { label: t("articleForm.adsEnabled"), active: adsEnabled },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <span className="text-hj-gray">{item.label}</span>
                    <span className={`text-xs font-medium ${item.active ? "text-emerald-600" : "text-gray-400"}`}>
                      {item.active ? t("articleForm.active") : t("articleForm.inactive")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-hj-gray-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-hj-dark">{t("articleForm.previewTitle")}</h3>
              <button onClick={() => setShowPreview(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
                <i className="ri-close-line text-hj-dark"></i>
              </button>
            </div>
            <div className="p-6">
              {selectedCategory && (
                <span className="inline-block px-3 py-1 text-xs font-semibold text-white rounded-md uppercase tracking-wide mb-4" style={{ backgroundColor: selectedCategory.color }}>
                  {selectedCategory.name}
                </span>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-hj-dark leading-tight font-heading">{title || "—"}</h1>
              {subtitle && <p className="text-lg text-hj-gray mt-2">{subtitle}</p>}
              <div className="flex items-center gap-4 mt-4 text-sm text-hj-muted flex-wrap">
                {selectedAuthor && (
                  <span className="flex items-center gap-2">
                    {selectedAuthor.image && <img src={selectedAuthor.image} alt={selectedAuthor.name} className="w-8 h-8 rounded-full object-cover" />}
                    <span className="font-medium text-hj-dark">{selectedAuthor.name}</span>
                  </span>
                )}
                <span>{publishedAt ? new Date(publishedAt).toLocaleDateString("de-CH") : new Date().toLocaleDateString("de-CH")}</span>
              </div>
              {imageUrl && (
                <div className="mt-6 rounded-xl overflow-hidden aspect-[16/9]">
                  <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="mt-6 space-y-4 text-base text-hj-dark leading-relaxed" dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, "<br/>") }} />
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-alert-line text-red-500 text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-hj-dark">{t("articleForm.deleteTitle")}</h3>
            </div>
            <p className="text-sm text-hj-gray mb-6">{t("articleForm.deleteConfirm")}</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
                {t("articleForm.cancel")}
              </button>
              <button onClick={confirmDelete} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors cursor-pointer">
                {t("articleForm.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEO Auto-prompt Modal */}
      {showSEOPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-magic-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-hj-dark">{t("articleForm.seoPromptTitle")}</h3>
              </div>
            </div>
            <p className="text-sm text-hj-gray mb-6">{t("articleForm.seoPromptDesc")}</p>

            {(() => {
              const emptyFields = [
                !metaTitle.trim() && t("articleForm.seoField.title"),
                !metaDescription.trim() && t("articleForm.seoField.description"),
                !focusKeyword.trim() && t("articleForm.seoField.keyword"),
                !imageAlt.trim() && t("articleForm.seoField.imageAlt"),
                !slug.trim() && t("articleForm.seoField.slug"),
              ].filter(Boolean) as string[];
              return emptyFields.length > 0 ? (
                <div className="mb-5 p-3 bg-hj-gray-light rounded-md">
                  <p className="text-xs text-hj-gray font-medium mb-2">{t("articleForm.seoConfirmDesc")}</p>
                  <div className="flex flex-wrap gap-2">
                    {emptyFields.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-hj-gray-border rounded-md text-xs text-hj-dark">
                        <i className="ri-add-circle-line text-hj-red text-xs"></i>
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            <div className="flex gap-3">
              <button
                onClick={skipSEOPrompt}
                className="flex-1 px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
              >
                {t("articleForm.seoPromptSkip")}
              </button>
              <button
                onClick={confirmSEOPromptGenerate}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-hj-red rounded-md hover:bg-hj-red-light transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-magic-line"></i>
                {t("articleForm.seoPromptGenerate")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEO Generate Modal */}
      {showSEOModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-hj-red/10 flex items-center justify-center">
                <i className="ri-magic-line text-hj-red text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-hj-dark">{t("articleForm.seoConfirmTitle")}</h3>
            </div>

            {(() => {
              const fields = [
                { key: "title", label: t("articleForm.seoField.title"), value: metaTitle },
                { key: "description", label: t("articleForm.seoField.description"), value: metaDescription },
                { key: "keyword", label: t("articleForm.seoField.keyword"), value: focusKeyword },
                { key: "imageAlt", label: t("articleForm.seoField.imageAlt"), value: imageAlt },
                { key: "slug", label: t("articleForm.seoField.slug"), value: slug },
              ];
              const empty = fields.filter((f) => !f.value);
              const filled = fields.filter((f) => f.value);

              return (
                <div className="space-y-4">
                  {empty.length > 0 && (
                    <div>
                      <p className="text-sm text-hj-dark font-medium mb-2">{t("articleForm.seoConfirmDesc")}</p>
                      <div className="space-y-1.5">
                        {empty.map((f) => (
                          <div key={f.key} className="flex items-center gap-2 text-sm px-3 py-2 bg-hj-gray-light rounded-md">
                            <i className="ri-add-circle-line text-hj-red text-xs"></i>
                            <span className="text-hj-dark">{f.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {filled.length > 0 && !seoOverwriteAll && (
                    <div>
                      <p className="text-sm text-hj-gray font-medium mb-2">{t("articleForm.seoSkippedDesc")}</p>
                      <div className="space-y-1.5">
                        {filled.map((f) => (
                          <div key={f.key} className="flex items-center gap-2 text-sm px-3 py-2 bg-gray-50 rounded-md">
                            <i className="ri-checkbox-circle-line text-emerald-500 text-xs"></i>
                            <span className="text-hj-gray">{f.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="flex items-center gap-3 p-3 rounded-md bg-hj-gray-light cursor-pointer">
                    <input
                      type="checkbox"
                      checked={seoOverwriteAll}
                      onChange={(e) => setSeoOverwriteAll(e.target.checked)}
                      className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                    />
                    <span className="text-sm text-hj-dark">{t("articleForm.seoOverwriteAll")}</span>
                  </label>
                </div>
              );
            })()}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSEOModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
              >
                {t("articleForm.seoCancelBtn")}
              </button>
              <button
                onClick={confirmSEOG}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-hj-red rounded-md hover:bg-hj-red-light transition-colors cursor-pointer"
              >
                {t("articleForm.seoGenerateBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
